class LazyVideo extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: contents;

        opacity: 0;
      
        transition: opacity 0.5s ease;
      }
      :host([loaded]) {
        opacity: 1;
      }
    `
  }

  connectedCallback () {
    super.connectedCallback()

    this.video = window.BAO.Utils.getElement('video', { context: this })

    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    })
    // We need to observe the video element as the host element has display: contents on it. IntersectionObserver won't work on an element that is set to display: contents
    this.observer.observe(this.video)
  }

  onEntry ([entry], observer) {
    if (!entry.isIntersecting) return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches

    if (prefersReducedMotion) {
      this.video.removeAttribute('autoplay')
      this.video.setAttribute('controls', '')
      this.video.pause()
    }

    for (const source in this.video.children) {
      const videoSource = this.video.children[source]

      if (
        typeof videoSource.tagName === 'string' &&
        videoSource.tagName === 'SOURCE'
      ) {
        videoSource.src = videoSource.dataset.src
      }
    }

    this.video.load()
    observer.unobserve(this.video)

    this.setAttribute('loaded', '')

    if (
      window.innerWidth > 768 ||
      this.video.hasAttribute('autoplay') === false
    ) {
      return
    }

    if (this.video.paused) {
      document.addEventListener('touchstart', () => {
        if (this.video.paused && !this.video.hasAttribute('initialised')) {
          this.video.play()
          this.video.setAttribute('initialised', true)
        }
      })
    }
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo)
}

class LazyVideoControls extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.handlePlayClick = this.handlePlayClick.bind(this)
    this.listeners.add(this.els.play.element, 'click', this.handlePlayClick)
  }

  handlePlayClick (event) {
    const section = event.target.closest(this?.externalEls?.section?.selector)

    if (!section) return

    const videos = Array.from(section.querySelectorAll('lazy-video')).filter(
      (el) => el.hasAttribute('loaded'),
    )

    section.setAttribute(
      'data-paused',
      videos.some((el) => !el.video.paused),
    )

    videos.forEach((el) => {
      const video = el.video
      const videoWasPaused = video.paused

      if (video.hasAttribute('data-play-one-lazy-video')) {
        const allVideos = document.querySelectorAll('[data-play-one-lazy-video]')

        allVideos.forEach((otherVideo) => {
          otherVideo.pause()
          this.updatePlayPauseButton(false, otherVideo.closest(this?.externalEls?.section?.selector))
        })
      }

      if (videoWasPaused) {
        video.play()
        this.updatePlayPauseButton(true, section)
      } else {
        if (video.paused === false) {
          video.pause()
          this.updatePlayPauseButton(false, section)
        }
      }
    })
  }

  updatePosterImage (isPlaying, section) {
    const posterImages = section.querySelectorAll(this.externalEls.poster.selector)

    posterImages.forEach((posterImage) => {
      posterImage.setAttribute('aria-hidden', isPlaying ? 'true' : 'false')
    })
  }

  updatePlayPauseButton (isPlaying, section) {
    const buttons = section.querySelectorAll(this.els.play.selector)

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', isPlaying ? 'false' : 'true')

      const playText = this.querySelector(this.els['play-text'].selector)
      playText.textContent = isPlaying ? this.dataset.pause : this.dataset.play
    })
  }
}

if (!customElements.get('lazy-video-controls')) {
  customElements.define('lazy-video-controls', LazyVideoControls)
}
